import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Copy, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    Loader2, RefreshCw, GitMerge, Users
} from 'lucide-react';
import { dataQualityService } from '../../services/dataQualityService';
import CustomSelect from '../shared/CustomSelect';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { ErrorAlert } from '../shared/ErrorAlert';
import { useAuth } from '../../context/AuthContext';
import { FormatUtils } from '../shared/FormatUtils';

const DuplicatesContent = () => {
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [paginating, setPaginating] = useState(false);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const fetchDuplicates = useCallback(async () => {
        try {
            setPaginating(true);
            setError('');

            const response = await dataQualityService.duplicates.scan(currentPage, pageSize);

            if (response.success && response.data) {
                const pageData = response.data;
                setGroups(pageData.content || []);
                setTotalElements(pageData.totalElements || 0);
                setTotalPages(pageData.totalPages || 0);
            }
        } catch (err) {
            console.error('Error scanning duplicates:', err);
            setError('Failed to scan for duplicates');
        } finally {
            setLoading(false);
            setPaginating(false);
        }
    }, [currentPage, pageSize]);

    useEffect(() => {
        fetchDuplicates();
    }, [fetchDuplicates]);

    const getConfidenceColor = (score) => {
        if (score >= 0.8) return 'bg-red-100 text-red-800';
        if (score >= 0.5) return 'bg-yellow-100 text-yellow-800';
        return 'bg-blue-100 text-blue-800';
    };

    const handleMerge = (group) => {
        // Navigate to merge workflow passing group data via state
        const primaryId = group.clients[0]?.id;
        if (primaryId) {
            navigate(`/data-quality/merge/${primaryId}`, {
                state: { duplicateGroup: group }
            });
        }
    };

    if (loading) {
        return <LoadingSpinner message="Scanning for duplicate clients..." />;
    }

    const startIndex = Math.min(currentPage * pageSize + 1, totalElements);
    const endIndex = Math.min((currentPage + 1) * pageSize, totalElements);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-semibold text-gray-900">Duplicate Detection</h2>
                    <p className="text-gray-600 mt-1">{totalElements.toLocaleString()} duplicate groups found</p>
                </div>
                <button
                    onClick={() => { setCurrentPage(0); fetchDuplicates(); }}
                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition flex items-center gap-2"
                >
                    <RefreshCw className="w-4 h-4" />
                    Rescan
                </button>
            </div>

            {error && <ErrorAlert error={error} onRetry={fetchDuplicates} />}

            {/* Duplicate Group Cards */}
            <div className="space-y-4 relative">
                {paginating && (
                    <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                    </div>
                )}

                {groups.length === 0 ? (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 text-center py-12">
                        <Copy className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">No duplicate groups found</p>
                        <p className="text-sm text-gray-400 mt-1">All client records appear to be unique</p>
                    </div>
                ) : (
                    groups.map((group, gIdx) => (
                        <div key={gIdx} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                            {/* Group Header */}
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-lg bg-red-100 text-red-600">
                                        <Users className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-gray-900">
                                            {group.clients?.length || 0} Matching Clients
                                        </p>
                                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                                            {(group.matchReasons || []).map((reason, rIdx) => (
                                                <span key={rIdx} className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
                                                    {reason}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className={`px-3 py-1 text-sm rounded-full font-medium ${getConfidenceColor(group.confidenceScore)}`}>
                                        {(group.confidenceScore * 100).toFixed(0)}% confidence
                                    </span>
                                    {hasPermission('DATA_QUALITY:MERGE') && (
                                        <button
                                            onClick={() => handleMerge(group)}
                                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2 text-sm"
                                        >
                                            <GitMerge className="w-4 h-4" />
                                            Compare & Merge
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Client Summaries */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                {(group.clients || []).map((client) => (
                                    <div
                                        key={client.id}
                                        className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition cursor-pointer"
                                        onClick={() => navigate(`/clients/${client.id}`)}
                                    >
                                        <div className="flex items-center justify-between mb-2">
                                            <p className="font-medium text-gray-900 text-sm">{client.name}</p>
                                            <span className={`px-2 py-0.5 text-xs rounded-full ${client.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                                                {client.active ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                        <div className="space-y-1 text-xs text-gray-500">
                                            <p>Code: {client.clientCode}</p>
                                            <p>Type: {client.clientType}</p>
                                            {client.email && <p>Email: {client.email}</p>}
                                            {client.phoneNumber && <p>Phone: {client.phoneNumber}</p>}
                                            {client.nationalId && <p>National ID: {client.nationalId}</p>}
                                            <p>Products: {client.productCount} | KYC Docs: {client.kycDocumentCount}</p>
                                            {client.createdAt && (
                                                <p>Created: {FormatUtils.formatDate(client.createdAt)}</p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Pagination */}
            {(totalPages > 0 || totalElements > 0) && (
                <div className="flex items-center justify-between px-4 py-3 bg-white rounded-lg border border-gray-200 sm:px-6">
                    <div className="flex items-center gap-4">
                        <span className="text-sm text-gray-700">
                            Showing <span className="font-medium">{startIndex.toLocaleString()}</span> to{' '}
                            <span className="font-medium">{endIndex.toLocaleString()}</span> of{' '}
                            <span className="font-medium">{totalElements.toLocaleString()}</span> groups
                        </span>
                        <CustomSelect
                            value={pageSize}
                            onChange={(val) => { setPageSize(Number(val)); setCurrentPage(0); }}
                            options={[
                                { value: 10, label: '10 per page' },
                                { value: 20, label: '20 per page' },
                                { value: 50, label: '50 per page' },
                            ]}
                            dropUp
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={() => setCurrentPage(0)} disabled={currentPage === 0 || paginating} className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition">
                            <ChevronsLeft className="w-5 h-5" />
                        </button>
                        <button onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 0 || paginating} className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition">
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <span className="px-4 py-2 text-sm text-gray-700">
                            Page {(currentPage + 1).toLocaleString()} of {totalPages.toLocaleString() || 1}
                        </span>
                        <button onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage >= totalPages - 1 || paginating} className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition">
                            <ChevronRight className="w-5 h-5" />
                        </button>
                        <button onClick={() => setCurrentPage(totalPages - 1)} disabled={currentPage >= totalPages - 1 || paginating} className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition">
                            <ChevronsRight className="w-5 h-5" />
                        </button>
                        {paginating && <Loader2 className="w-5 h-5 animate-spin text-blue-600 ml-2" />}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DuplicatesContent;
